import { ChangeDetectionStrategy, Component, computed, EventEmitter, inject, input, Output, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoModule } from '@jsverse/transloco';
import { NgxPermissionsService } from 'ngx-permissions';
import { GuidelineViewService, LicenseService, RolePermissions } from 'shared/domain';
import { IconDirective, TooltipDirective } from 'shared/ui-common';
import { IsMobileService } from 'shared/util-common';

import { NetworkStatusService } from '../../../../../util-common/src/lib/services/network-status.service';

export enum FlowchartToolbarEvent {
  ZOOM_IN = 'ZOOM_IN',
  ZOOM_OUT = 'ZOOM_OUT',
  SCALE_CONTENT_TO_FIT = 'SCALE_CONTENT_TO_FIT',
  FOCUS_START = 'FOCUS_START',
  EXPORT = 'EXPORT',
  EXPORT_PDF = 'EXPORT_PDF',
  EXPAND = 'EXPAND',
}

@Component({
  selector: 'tt-flowchart-toolbar',
  standalone: true,
  imports: [MatTooltipModule, TranslocoModule, MatIconModule, IconDirective, TooltipDirective],
  templateUrl: './toolbar.component.html',
  styleUrls: ['./toolbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlowchartToolbarComponent {
  protected isMobile = inject(IsMobileService).isMobile;
  protected isOnline = inject(NetworkStatusService).isOnline;
  protected licenseService = inject(LicenseService);
  private permissionsService = inject(NgxPermissionsService, { optional: true });
  private isPreviewDialogMode = inject(GuidelineViewService, { optional: true })?.isPreviewDialogMode;

  public isEditable = input<boolean>();
  public fullScreenMode = input<boolean>(false);

  @Output() public toolbarItemClicked = new EventEmitter<FlowchartToolbarEvent>();

  protected expanded = signal(false);

  protected iconSize = computed(() => (this.isMobile() ? 18 : 14));

  protected canExportAsPng = computed(() => !this.fullScreenMode() && this.isOnline());
  protected canExportPdf = computed(() => {
    if (!this.isOnline()) {
      return false;
    }

    if (this.isEditable()) {
      return false;
    }

    if (this.isPreviewDialogMode?.()) {
      return false;
    }

    if (!this.licenseService.licensedFeatures().pdfExport) {
      return false;
    }

    return this.permissionsService?.getPermission(RolePermissions.exportPdf);
  });

  protected handleToolbarItemClicked = (event: FlowchartToolbarEvent) => {
    if (event !== FlowchartToolbarEvent.ZOOM_IN && event !== FlowchartToolbarEvent.ZOOM_OUT) {
      this.expanded.set(false);
    }

    this.toolbarItemClicked.emit(event);
  };

  protected FlowchartToolbarEvent = FlowchartToolbarEvent;
}
